import { Component, Input, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { find } from "lodash";
import { FacilityDTO } from "src/app/facilities/Facility";
import { FlowsService } from "src/app/flows/flows.service";
import { FacilitySelectDialogComponent } from "src/app/shared/dialogs/facility-select-dialog/facility-select-dialog.component";
import { environment } from "src/environments/environment";
import { FlowMenuComponent } from "../../flow-menu/flow-menu.component";

export function RequiredIfTriggerNeedsLocation(
  formGroup: FormGroup,
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if(formGroup.controls.trigger.value === null){
      return null;
    }
    if (
      formGroup.controls.trigger.value.location_required
    ) {
      if (
        control.value === undefined ||
        control.value === null ||
        control.value === ""
      ) {
        return {
          valueUndefined: "Value must be defined",
        };
      }
    }
    return null;
  };
}

export function RequiredIfTriggerNeedsInterval(
  formGroup: FormGroup,
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if(formGroup.controls.trigger.value === null){
      return null;
    }
    if (
      formGroup.controls.trigger.value.interval_required
    ) {
      if (
        control.value === undefined ||
        control.value === null ||
        control.value === ""
      ) {
        return {
          valueUndefined: "Value must be defined",
        };
      }
    }
    return null;
  };
}

export function RequiredIfTriggerIsAPIRequest(
  formGroup: FormGroup,
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const trigger = formGroup.controls.trigger.value;
    if (trigger === null) {
      return null;
    }
    if (trigger.name === "APIRequest") {
      if (
        control.value === undefined ||
        control.value === null ||
        control.value === ""
      ) {
        return {
          valueUndefined: "Value must be defined",
        };
      }
    }
    return null;
  };
}

const API_ID_LENGTH = 12;
const API_AUTH_LENGTH = 48;
const ALPHANUMERIC =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

@Component({
  selector: "app-starter-node-menu",
  templateUrl: "./starter-node-menu.component.html",
  styleUrls: ["./starter-node-menu.component.scss"],
})
export class StarterNodeMenuComponent
  extends FlowMenuComponent
  implements OnInit
{
  @Input() facilities!: FacilityDTO[];
  starterNodeForm!: FormGroup;
  hourOptions!: string[];
  minuteOptions: string[] = [];
  pmOptions!: { name: string; code: boolean }[];
  locationOptions: any;
  triggerOptions: any;

  constructor(
    private FlowsService: FlowsService,
    private dialog: MatDialog,
  ) {
    super();
  }

  private randomAlphanumeric(length: number): string {
    const cryptoObj = window.crypto || (window as any).msCrypto;
    if (!cryptoObj || !cryptoObj.getRandomValues) {
      let fallback = "";
      for (let i = 0; i < length; i++) {
        fallback += ALPHANUMERIC[Math.floor(Math.random() * ALPHANUMERIC.length)];
      }
      return fallback;
    }
    const randomBytes = new Uint8Array(length);
    cryptoObj.getRandomValues(randomBytes);
    let result = "";
    for (let i = 0; i < length; i++) {
      result += ALPHANUMERIC[randomBytes[i] % ALPHANUMERIC.length];
    }
    return result;
  }

  private ensureAPIRequestCredentials(triggerValue: any) {
    if (!triggerValue || triggerValue.name !== "APIRequest") {
      return;
    }
    if (!this.starterNodeForm.controls.apiID.value) {
      this.starterNodeForm.controls.apiID.setValue(
        this.randomAlphanumeric(API_ID_LENGTH),
      );
    }
    if (!this.starterNodeForm.controls.apiAuth.value) {
      this.starterNodeForm.controls.apiAuth.setValue(
        this.randomAlphanumeric(API_AUTH_LENGTH),
      );
    }
  }

  ngOnInit(): void {

    //console.log('STARTER NODE MENU INIT')

    super.ngOnInit();

    this.hourOptions = [
      "12",
      "01",
      "02",
      "03",
      "04",
      "05",
      "06",
      "07",
      "08",
      "09",
      "10",
      "11",
    ];

    this.pmOptions = [
      {
        name: "AM",
        code: false,
      },
      {
        name: "PM",
        code: true,
      },
    ];

    this.updateLocationOptions(null);

    this.triggerOptions = this.FlowsService.triggerOptions.filter((t: any)=>{
      return t.internal == this.internal;
    });

    this.facilities.forEach((facility: FacilityDTO) => {
      facility.selected = this.node.data?.flowData?.facilities?.includes(
        facility.id,
      );
    });

    let startDate,
      startHour,
      startMinute,
      startPM,
      endDate,
      endHour,
      endMinute,
      endPM,
      trigger;

    if (this.node.data?.flowData) {
      startDate = new Date(this.node.data.flowData.startDate);

      startHour = startDate.getHours();
      startMinute = startDate.getMinutes();
      startPM = startHour >= 12;
      startHour = startHour % 12;
      startHour = startHour ? startHour : 12;
      startHour = startHour < 10 ? "0" + startHour : startHour.toString();
      startMinute =
        startMinute < 10 ? "0" + startMinute : startMinute.toString();

      endDate = new Date(this.node.data.flowData.endDate);

      endHour = endDate.getHours();
      endMinute = endDate.getMinutes();
      endPM = endHour >= 12;
      endHour = endHour % 12;
      endHour = endHour ? endHour : 12;
      endHour = endHour < 10 ? "0" + endHour : endHour.toString();
      endMinute = endMinute < 10 ? "0" + endMinute : endMinute.toString();

      trigger = find(this.triggerOptions, {id: this.node.data.flowData.entryEventTypeID});

      this.FlowsService.currentFlowTrigger = trigger;

      this.updateLocationOptions(trigger);

    }

    this.starterNodeForm = new FormGroup({
      flowName: new FormControl(this.node.data?.flowData?.name, [
        Validators.required,
      ]),
      startDate: new FormControl(startDate, [Validators.required]),
      startDateHour: new FormControl(startHour, [Validators.required]),
      startDateMinute: new FormControl(startMinute, [Validators.required]),
      startDatePM: new FormControl(startPM, [Validators.required]),

      endDate: new FormControl(endDate, [Validators.required]),
      endDateHour: new FormControl(endHour, [Validators.required]),
      endDateMinute: new FormControl(endMinute, [Validators.required]),
      endDatePM: new FormControl(endPM, [Validators.required]),

      trigger: new FormControl(trigger, [Validators.required]),

    });

    this.starterNodeForm.get("startDate")!.valueChanges.subscribe(value => {
      if(this.starterNodeForm.get("startDateHour")?.value == null){
        this.starterNodeForm.get("startDateHour")?.setValue("12");
        this.starterNodeForm.get("startDateMinute")?.setValue("00");
        this.starterNodeForm.get("startDatePM")?.setValue(this.pmOptions[0].code);
      }
    });

    this.starterNodeForm.get("endDate")!.valueChanges.subscribe(value => {
      if(this.starterNodeForm.get("endDateHour")?.value == null){
        this.starterNodeForm.get("endDateHour")?.setValue("12");
        this.starterNodeForm.get("endDateMinute")?.setValue("00");
        this.starterNodeForm.get("endDatePM")?.setValue(this.pmOptions[0].code);
      }
    });

    this.starterNodeForm.get("trigger")?.valueChanges.subscribe(value => {
      this.FlowsService.currentFlowTrigger = value;
      this.updateLocationOptions(value);
      this.ensureAPIRequestCredentials(value);
      this.starterNodeForm.controls.apiID.updateValueAndValidity();
      this.starterNodeForm.controls.apiAuth.updateValueAndValidity();
    })

    for (let i = 0; i < 60; i++) {
      if (i < 10) {
        this.minuteOptions.push(`0${i}`);
      } else {
        this.minuteOptions.push(i.toString());
      }
    }

    if (this.facilities.length === 1) {
      this.facilities[0].selected = true;
    }

    let location;
    if(trigger != null) {
      if (trigger.location_required && trigger.name.indexOf('Geofence') > -1) {
        location = this.locationOptions.find((location: any) => {
          return (
            location.type === "geofence" &&
            location.id === this.node.data.ids[0]
          );
        });
      }
      else if (trigger.location_required && trigger.name.indexOf('BeaconRegion') > -1) {
        location = this.locationOptions.find((location: any) => {
          return (
            location.type === "beacon-region" &&
            location.id === this.node.data.ids[0]
          );
        });
      }
    }
    

    this.starterNodeForm.addControl(
      "triggerLocation",
      new FormControl(location, [
        RequiredIfTriggerNeedsLocation(this.starterNodeForm),
      ]),
    );

    let days, hours, minutes;

    if(trigger != null && trigger.interval_required && this.node.data.flowData.entryEventInterval !== undefined) {
      days = this.node.data.flowData.entryEventInterval.days;
      hours = this.node.data.flowData.entryEventInterval.hours;
      minutes = this.node.data.flowData.entryEventInterval.minutes;
    }

    this.starterNodeForm.addControl(
      "intervalDays",
      new FormControl(days, [
        RequiredIfTriggerNeedsInterval(this.starterNodeForm),
      ]),
    );

    this.starterNodeForm.addControl(
      "intervalHours",
      new FormControl(hours, [
        RequiredIfTriggerNeedsInterval(this.starterNodeForm),
      ]),
    );

    this.starterNodeForm.addControl(
      "intervalMinutes",
      new FormControl(minutes, [
        RequiredIfTriggerNeedsInterval(this.starterNodeForm),
      ]),
    );

    this.starterNodeForm.addControl(
      "apiID",
      new FormControl(this.node.data?.flowData?.apiID, [
        RequiredIfTriggerIsAPIRequest(this.starterNodeForm),
      ]),
    );

    this.starterNodeForm.addControl(
      "apiAuth",
      new FormControl(this.node.data?.flowData?.apiAuth, [
        RequiredIfTriggerIsAPIRequest(this.starterNodeForm),
      ]),
    );

    this.ensureAPIRequestCredentials(trigger);

  }

  updateLocationOptions(value: any) {
    this.locationOptions = this.FlowsService.locationOptions.filter((l: any)=>{
      if(value === null){
        return true;
      }
      return value.name.indexOf('Geofence') > -1 ? l.type === 'geofence' : l.type === 'beacon-region';
    });
  }

  get locationPlaceholder() {
    if (this.entryEventType.name.indexOf('Geofence') > -1){
      return 'Geofence';
    }
    if (this.entryEventType.name.indexOf('BeaconRegion') > -1){
      return 'Beacon Region';
    }
    return 'Location'
  }

  get invalid() {
    return !(
      this.starterNodeForm?.valid &&
      (!this.entryEventType.internal && this.facilities?.length ? !!this.selectedFacilities?.length : true)
    );
  }

  get selectedFacilities() {
    return this.facilities.filter((facility: any) => facility.selected);
  }

  public get trigger() {
    if(this.starterNodeForm === undefined){
      return null;
    }
    return this.starterNodeForm.controls.trigger.value;
  }

  get entryObjectID() {
    const trigger = this.trigger;
    if (trigger.name.indexOf("Entry") > -1 || trigger.name.indexOf("Exit") > -1) {
      return this.starterNodeForm.controls.triggerLocation.value.id;
    }
    return null;
  }

  get nodeDTO() {
    const location = this.starterNodeForm.controls.triggerLocation.value;
    return {
      id: this.node.id,
      type: "start",
      data: {
        event: this.starterNodeForm.controls.trigger.value,
        ids: this.entryObjectID ? [this.entryObjectID] : [],
        info: location
          ? {
              id: `${location.type}-${location.id}`,
              identifier: `${location.type}:${location.id}`,
              int: location.id,
              location: location.name,
              name: location.name,
              type: location.type === "geofence" ? "Geofence" : "Beacon Region",
            }
          : null,
      },
      top: this.node.top,
      left: this.node.left,
    };
  }

  get flowDTO() {
    const to24Hour = (hour12: string, isPM: boolean): number => {
      const normalizedHour = Number.parseInt(hour12, 10) % 12;
      return isPM ? normalizedHour + 12 : normalizedHour;
    };

    const startDate: Date = this.starterNodeForm.controls.startDate.value;
    const startHour = to24Hour(
      this.starterNodeForm.controls.startDateHour.value,
      this.starterNodeForm.controls.startDatePM.value,
    );
    const startMinutes = Number.parseInt(
      this.starterNodeForm.controls.startDateMinute.value,
    );
    startDate.setHours(startHour, startMinutes);

    const endDate: Date = this.starterNodeForm.controls.endDate.value;
    const endHour = to24Hour(
      this.starterNodeForm.controls.endDateHour.value,
      this.starterNodeForm.controls.endDatePM.value,
    );
    const endMinutes = Number.parseInt(
      this.starterNodeForm.controls.endDateMinute.value,
    );
    endDate.setHours(endHour, endMinutes);

    return {
      name: this.starterNodeForm.controls.flowName.value,
      start_date: startDate.toISOString(),
      start_time: startDate.toISOString(),
      end_date: endDate.toISOString(),
      end_time: endDate.toISOString(),
      facilities: this.facilities
        .filter((facility) => facility.selected)
        .map((facility) => facility.id),
      entry_event_object_ids: this.entryObjectID ? [this.entryObjectID] : [],
      entry_event_type_id: this.trigger.id,
      entry_event_interval: this.trigger.interval_required ? {
        days: parseInt(this.starterNodeForm.controls.intervalDays.value),
        hours: parseInt(this.starterNodeForm.controls.intervalHours.value),
        minutes: parseInt(this.starterNodeForm.controls.intervalMinutes.value)
      } : null,
      api_id: this.isAPIRequestTrigger
        ? this.starterNodeForm.controls.apiID.value
        : null,
      api_auth: this.isAPIRequestTrigger
        ? this.starterNodeForm.controls.apiAuth.value
        : null,
    };
  }

  get entryEventType() {
    return this.trigger;
  }

  get isAPIRequestTrigger() {
    return this.trigger?.name === "APIRequest";
  }

  get apiID() {
    return this.starterNodeForm?.controls.apiID?.value || "";
  }

  get apiAuth() {
    return this.starterNodeForm?.controls.apiAuth?.value || "";
  }

  get apiRequestURL() {
    const baseURL = (environment.apiURL || "").replace(/\/$/, "");
    return `${baseURL}/api/v1/flow/${this.apiID}`;
  }

  get apiAuthHeaderValue() {
    return `x-access-token: ${this.apiAuth}`;
  }

  copyToClipboard(value: string) {
    if (!value) {
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).catch(() => {
        this.fallbackCopyToClipboard(value);
      });
      return;
    }
    this.fallbackCopyToClipboard(value);
  }

  private fallbackCopyToClipboard(value: string) {
    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "absolute";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }

  selectFacilities() {
    const dialogRef = this.dialog.open(FacilitySelectDialogComponent, {
      width: "500px",
      data: {
        facilities: this.facilities
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.facilities = result;
      }
    });
  }

}
